import store from '@store'
import formatCurrency from '@utils/format-currency'
import formatDate from '@utils/format-date'
import formatPaymentMethod from '@utils/format-payment-method'
import parseISO from 'date-fns/parseISO'
import { conditionList } from "@utils/is-condition";
import formatPrice from "@utils/format-price";
import { formatOrderStatusTitle, isHotelOrderPendingEvaluation } from "@utils/format-order";
import formatGender, { formatPetGender } from "@utils/format-gender";
import { memberLevelPriceDiscount } from "@utils/ipetbooking-level";

/**
 * 是否可以訂單結帳
 * @param orderItem
 * @returns {*|boolean}
 */
export function orderIsCanPay({ paymentExpiredDate = null, allowExpired = 0 } = {}) {
  const isNoExpiredDate = !paymentExpiredDate;
  if(isNoExpiredDate) {
    return true;
  } else {
    const isExpired = parseISO(paymentExpiredDate) <= new Date();

    return allowExpired === 1 || isNoExpiredDate || !isExpired
  }
}

/**
 * 是否是線下付款
 * @param order
 * @param ordertype
 * @param type
 * @param isList
 * @returns {boolean}
 */
export function orderIsOffline({ order, ordertype, type, isList = false } = {}) {
  const merchantCashFlow = store.state.merchant.currentMerchant?.cashflowSetting
  const isNotStoreCashflow = () => {
    switch(ordertype) {
      case 'cart':
        switch(type) {
          case 'prepaid':
            return order.prepaid.stores[0]?.cashflowSetting !== 1;
          case 'reservation':
            return merchantCashFlow !== 1;
          case 'deposit':
            return merchantCashFlow !== 1;
          default:
            return true;
        }
      case 'order':
        switch(type) {
          case 'prepaid':
            if(isList) {
              return order.prepaid?.stores[0]?.cashflowSetting !== 1;
            } else {
              return order.stores[0]?.cashflowSetting !== 1;
            }
          case 'reservation':
            // return order.param.store.cashflowSetting !== 1;
            return merchantCashFlow !== 1;
          case 'deposit':
            return merchantCashFlow !== 1;
          case 'hotel':
            return merchantCashFlow !== 1;
          default:
            return true;
        }
      default:
        return true
    }
  };

  return isNotStoreCashflow();
}

/**
 * 是否可使用這方式付款
 * @param order
 * @param ordertype
 * @param type
 * @param isList
 * @param payment
 * @returns {boolean}
 */
export function orderIsCanUseThisPayment({ order, ordertype, type, isList = false, payment } = {}) {
  // const isPlanAliasNormal = store.state.merchant.currentMerchant?.planAlias === 'normal';
  const paymentMethods = store.state.merchant.currentMerchant?.paymentMethods
  const isCanUseThisPayment = () => {
    switch(ordertype) {
      case 'cart':
        switch(type) {
          case 'prepaid':
            return order.prepaid.stores[0]?.paymentMethods.includes(payment);
          case 'reservation':
          case 'daycare' :
            if(order.uuid) {
              return order.paymentMethods.includes(payment);
            } else {
              return order.param.store.paymentMethods.includes(payment);
            }
          case 'deposit':
            return paymentMethods.includes(payment);
          case 'hotel':
            return Array.isArray(order.paymentMethods) && order.paymentMethods.includes(payment);
          default:
            return false;
        }
      case 'order':
        switch(type) {
          case 'prepaid':
            if(isList) {
              return order.prepaid?.stores[0]?.paymentMethods.includes(payment);
            } else {
              return order.stores[0]?.paymentMethods.includes(payment);
            }
          case 'reservation':
          case 'daycare' :
            if(order.uuid) {
              return order.paymentMethods.includes(payment);
            } else {
              return order.param.store.paymentMethods.includes(payment);
            }
          case 'deposit':
            return paymentMethods.includes(payment);
          case 'hotel':
            return Array.isArray(order.paymentMethods) && order.paymentMethods.includes(payment);
          default:
            return false;
        }
      default:
        return false
    }
  };

  return isCanUseThisPayment()
}

/**
 * 是否是包套抵扣訂單
 * @param orderParams
 * @returns {*|boolean}
 */
export function orderIsUseDeduct({ params } = {}) {
  return params && params[0]
    ? params.some(item => {
      return item.type === 'prepaid'
    })
    : false
}

/**
 * 是否是未付款包套抵扣訂單
 * @param paymentStatus
 * @param params
 * @returns {false|*|boolean|boolean}
 */
export function orderIsPrepaidUnpaidAndDeduct({ prepaid: { status } = {}, params } = {}) {
  return status && params && params[0]
    ? status === 2 && orderIsUseDeduct({ params })
    : false
}

/**
 * Vacc 資訊
 * @param Vacc
 * @param paymentExpiredDate
 * @param unpaid
 * @returns {[{text: string, title: string}, {text: *, title: string}, {text, title: string}, {text: string, title:
 *   string}, {text: string, type: string, class: string}]|*[]}
 */
export function orderVaccInfoList({ Vacc, paymentExpiredDate, unpaid } = {}) {
  const orderVaccInfoList = Vacc && paymentExpiredDate && unpaid
    ? [
      {
        title: '匯款銀行',
        text: `${Vacc.Bank} ${Vacc.BankCode}`,
      },
      {
        title: '匯款帳號',
        text: Vacc.VACCNo,
      },
      {
        title: '匯款期限',
        text: formatDate(paymentExpiredDate, 'yyyy/MM/dd HH:mm'), // '2020/01/01 00:59:59'
      },
      {
        title: '匯款金額',
        text: `NT$ ${formatCurrency(unpaid)}`,
      },
      {
        type: 'description',
        text: '請您依照指定的帳號轉入款項，金額需相符不可溢繳。付款後約1小時可於會員中心查詢是否付款成功。',
        class: 'text-gray-2 mt-3'
      }
    ]
    : [];

  return conditionList(orderVaccInfoList);
}

/**
 * Atm 資訊
 * @param Atm
 * @param paymentExpiredDate
 * @param unpaid
 * @returns {[{text: string, title: string}, {text: *, title: string}, {text: string, title: string}, {text, title:
 *   string}, {text: string, title: string}, null]|*[]}
 */
export function orderAtmInfoList({ Atm, paymentExpiredDate, unpaid } = {}) {
  const orderAtmInfoList = Atm && unpaid
    ? [
      {
        title: '匯款銀行',
        text: `${Atm.Bank} ${Atm.BankCode}`,
      },
      {
        title: '匯款帳號',
        text: Atm.VACCNo,
      },
      {
        title: '戶名',
        text: Atm.FullName,
      },
      {
        title: '匯款期限',
        text: formatDate(paymentExpiredDate, 'yyyy/MM/dd HH:mm'), // '2020/01/01 00:59:59'
      },
      {
        title: '匯款金額',
        text: `NT$ ${formatCurrency(unpaid)}`,
      },
      {
        type: 'description',
        text: '請您依照指定的帳號轉入款項，金額需相符不可溢繳。完成付款後請與我們確認是否入帳，謝謝。',
        class: 'text-gray-2 mt-3'
      }
    ]
    : [];

  return conditionList(orderAtmInfoList);
}

/**
 * 寵物資訊
 * @param name
 * @param gender
 * @param categoryTitle
 * @param sizeTitle
 * @param breed
 * @param birthday
 * @returns {*}
 */
export function orderPetInfoList({ pet: { name, gender, categoryTitle, sizeTitle, breed, birthday } } = {}) {
  return conditionList([
    {
      title: '寵物名',
      text: name || '----',
    },
    {
      title: '性別',
      text: formatPetGender(gender) || '----',
    },
    {
      title: '類型',
      text: categoryTitle || '----',
    },
    {
      title: '體型',
      text: sizeTitle || '----',
    },
    {
      title: '品種',
      text: breed || '----',
    },
    {
      title: '生日',
      text: formatDate(birthday) || '----',
    },
  ])
}

/**
 * 預約資訊
 * @param fullName
 * @param gender
 * @param phoneCode
 * @param phoneNumber
 * @param email
 * @param note
 * @returns {*}
 */
export function orderReservationInfoList({ fullName, gender, phoneCode, phoneNumber, email, orders } = {}) {
  return conditionList([
    {
      title: '貴賓姓名',
      text: fullName && gender
          ? `${fullName} ${formatGender(gender)}`
          : '----',
    },
    {
      title: '貴賓電話',
      text: phoneCode && phoneNumber
        ? `${phoneCode} ${phoneNumber}`
        : '----',
    },
    {
      title: '貴賓Email',
      text: email || '----',
    },
    {
      title: '備註',
      text: orders.items[0].note || '----',
    },
  ])
}

/**
 * 訂單資訊
 * @param status
 * @param paymentExpiredDate
 * @param createdDate
 * @param created
 * @param paidDate
 * @param cancelDate
 * @param paymentMethod
 * @returns {[{condition: (function()), text, title: string, class: string}, {condition: (function()), text, title:
 *   string}, {condition: (function(): boolean), text, title: string}, {condition: (function(): boolean), text, title:
 *   string}, {condition: (function(): boolean), text, title: string}]|*[]}
 */
export function orderInfoList({
  status,
  paymentExpiredDate,
  createdDate,
  paidDate,
  cancelDate,
  paymentStatus,
  paymentMethod,
  note,
  type,
  orders,
  isContainer = false
} = {}) {
  const hotelCtx = { type, orders }
  const paymentStateTitle = formatOrderStatusTitle(
    cancelDate ? 0 : status,
    paymentStatus,
    cancelDate,
    hotelCtx
  )
  // const isHasStatus = isContainer ? true : status
  return (status || paymentStatus) && (paymentMethod || paymentMethod === null) && (paymentExpiredDate || createdDate || paidDate || cancelDate)
    ? conditionList([
      {
        title: '付款狀態',
        text: isContainer ? paymentStateTitle : formatOrderStatusTitle(status, paymentStatus, cancelDate, hotelCtx),
        class: isContainer
          ? (isHotelOrderPendingEvaluation(hotelCtx) ? 'text-warning' : paymentStateTitle === '未付款' ? 'text-danger' : '')
          : formatOrderStatusTitle(status, paymentStatus, cancelDate, hotelCtx) === 'UNPAID'
          ? 'text-danger'
          : '',
        condition: () => {
          return true;
        },
      },
      {
        title: '付款方式',
        text: formatPaymentMethod(paymentMethod),
        condition: () => {
          return true;
        },
      },
      {
        title: '訂單付款期限',
        text: formatDate(paymentExpiredDate, 'yyyy/MM/dd EEE HH:mm'),
        class: 'text-danger',
        condition: () => {
          return !!paymentExpiredDate && (status === 2 || paymentStatus === 'UNPAID') && status !== 0 && paymentStatus !== 'PAID';
        },
      },
      {
        title: '訂單成立時間',
        text: formatDate(createdDate, 'yyyy/MM/dd EEE HH:mm'),
        condition: () => {
          return !!createdDate;
        },
      },
      {
        title: '訂單完成時間',
        text: formatDate(paidDate, 'yyyy/MM/dd EEE HH:mm'),
        condition: () => {
          return !!paidDate;
        },
      },
      {
        title: '訂單取消時間',
        text: formatDate(cancelDate, 'yyyy/MM/dd EEE HH:mm'),
        condition: () => {
          return !!cancelDate;
        },
      },
      {
        title: '備註',
        text: note,
        condition: () => {
          return !!note;
        },
      },


    ])
    : [];
}

/**
 * 訂單項目
 * @param typeList
 * @param params
 * @param isUnpaidAndDeduct
 * @returns {*}
 */
export function orderParamsList({
  typeList = [],
  params,
  isUnpaidAndDeduct = false
} = {}) {
  const paramList = params.filter(item => {
    return typeList.includes(item.type) || !typeList[0];
  });
  return paramList.map(item => {
    const memberLevelDiscpuntStyle = (numberType) => {
      return `
      <span>會員價：${ numberType === 'negative' 
        ? `NT$ -${Math.abs(item.total)}`: `NT$ ${item.total}`}</span>
      <br>
      <span class="font-number text-decoration-line-through fs-2sm text-gray-2 text-align-right">售價:${ numberType === 'negative' 
        ? `NT$ -${Math.abs(item.price)}`: `NT$ ${item.price}`}</span>
      `
    }
    const defaultItem = {
      text: item.levelDiscount > 0 && item.levelDiscount !== null && typeList.length > 0
      ? memberLevelDiscpuntStyle(item.total < 0 ? 'negative' : '') 
      : item.total < 0
        ? `- NT$ ${formatCurrency(Math.abs(item.total))}`
        : `NT$ ${formatCurrency(item.total)}`,
      class: item.total < 0
        ? 'text-danger'
        : '',
      textClass: 'font-number',
    };
    switch(item.type) {
      case 'cosmetic':
        return {
          ...defaultItem,
          title: `${item.qty}<i class="fontello fontello-paw fa-fw ml-2 text-primary"></i>`,
          shortTitle: `${item.qty}<i class="fontello fontello-paw fa-fw ml-2 text-primary"></i>`,
        };
      case 'prepaid-plan':
        return {
          ...defaultItem,
          title: `${item.qty}<i class="fontello fontello-paw fa-fw ml-2 text-primary"></i>`,
          shortTitle: `${item.qty}<i class="fontello fontello-paw fa-fw ml-2 text-primary"></i>`,
        };
      case 'prepaid':
        return {
          ...defaultItem,
          title: `${item.description} - 抵用1次${isUnpaidAndDeduct ? '(未付款)' : ''}`,
          shortTitle: '包套抵用',
        };
      case 'deposit':
        return {
          ...defaultItem,
          title: '儲值金付款',
          shortTitle: '儲值金付款',
        };
      case 'deposit-plan':
        return {
          ...defaultItem,
          title: '儲值金付款',
          shortTitle: '儲值金付款',
        }; 
      case 'coupon':
        return {
          ...defaultItem,
          title: `優惠券 ${item.title}`,
          shortTitle: '優惠券',
        };
      case 'cosmetic-add':
        return {
          ...defaultItem,
          title: `加購 - ${item.title}`,
          shortTitle: `+${item.title}`,
        };
      case '+':
        return {
          ...defaultItem,
          title: `加價`,
          shortTitle: `加價`,
        };
      case '-':
        return {
          ...defaultItem,
          title: `減價`,
          shortTitle: `減價`,
        };
      case '%':
        return {
          ...defaultItem,
          title: `折扣(${item.description}%)`,
          shortTitle: `折扣(${item.description}%)`,
        };
      default:
        return false
    }
  });
}

/**
 * 訂單明細
 * @param type
 * @param params
 * @param priceTotal
 * @param prepaid
 * @param isUnpaidAndDeduct
 * @param formatOrder
 * @returns {*}
 */
export function orderBillingReservationList({
  type = 'memberOrder', // [order, memberOrder],
  params,
  priceTotal,
  isUnpaidAndDeduct = false,
  formatOrder = {} // type: order required
} = {}) {
  const paramsList = (typeList) => {
    return orderParamsList({
      typeList,
      params,
      isUnpaidAndDeduct
    });
  };

  const subTotal = params
    .filter(item => {
      return ['cosmetic', 'coupon', 'prepaid', 'deposit','deposit-plan','prepaid-plan'].includes(item.type)
    })
    .reduce((previousValue, currentValue) => {
      return previousValue + currentValue.total
    }, 0);
  return conditionList([
    ...paramsList(['cosmetic', 'coupon', 'prepaid', 'deposit','deposit-plan','prepaid-plan']),
    {
      type: 'hr',
      condition: () => {
        return !!paramsList(['cosmetic', 'coupon', 'prepaid', 'deposit','prepaid-plan'])[0]
      },
    },
    {
      title: '小計',
      text: `NT$ ${formatCurrency(subTotal)}`,
      textClass: 'font-number',
      condition: () => {
        return !!paramsList(['cosmetic', 'coupon', 'prepaid', 'deposit','prepaid-plan'])[0]
      },
    },
    ...paramsList(['cosmetic-add', '+', '-', '%','daycare-add']),
    {
      type: 'hr',
      condition: () => {
        return !!paramsList(['cosmetic', 'coupon', 'prepaid', 'deposit','deposit-plan','prepaid-plan'])[0]
      },
    },
    {
      title: '總計',
      text: `${formatPrice(priceTotal)}`,
      textClass: 'font-number',
      condition: () => {
        return type === 'memberOrder'
      },
    },
    {
      title: `<span class="text-${formatOrder.color} fw-semi-bold">${formatOrder.title}</span><br><span class="text-gray-2 fs-2sm">${formatOrder.paidInfo}</span>`,
      text: `${formatPrice(priceTotal)}`,
      class: 'align-items-end mt-2',
      textClass: `font-number fs-2xl fw-semi-bold text-${formatOrder.infoColor}`,
      condition: () => {
        return type === 'order'
      },
    }
  ]);
}


/**
 * 多隻預約訂單項目
 * @param typeList
 * @param params
 * @param isUnpaidAndDeduct
 * @returns {*}
 */
export function orderReserveParamsList({
  typeList = [],
  params,
  isUnpaidAndDeduct = false
} = {}) {
  const paramList = params.filter(item => {
    return typeList.includes(item.type) || !typeList[0];
  });
  return paramList.map(item => {
    const memberLevelDiscpuntStyle = (numberType) => {
      return `
      <span>會員價：${ numberType === 'negative' 
        ? `NT$ -${Math.abs(item.total)}`: `NT$ ${memberLevelPriceDiscount(item.price, item)}`}</span>
      <br>
      <span class="font-number text-decoration-line-through fs-2sm text-gray-2 text-align-right">售價:${ numberType === 'negative' 
        ? `NT$ -${Math.abs(item.price)}`: `NT$ ${item.price}`}</span>
      `
    }
    const text = () => {
      return `${item.qty > 1 ? `${item.qty} x ` : ''}  ` + `${item.levelDiscount > 0 && item.levelDiscount !== null && typeList.length > 0
      ? memberLevelDiscpuntStyle(item.total < 0 ? 'negative' : '') 
      : item.total < 0
        ? `- NT$ ${formatCurrency(Math.abs(item.total))}`
        : `NT$ ${formatCurrency(item.price)}`}`
    }
    const defaultItem = {
      text: text(),
      class: item.total < 0
      ? 'text-danger'
      : '',
      textClass: 'font-number',
      qty: item.qty,
      serviceTime: item.serviceTime,
      // type: item.type,
      price: item.price,
      total: item.total,
      category: item.type,
      levelDiscount: item.levelDiscount || 0,
      levelDiscountType: item.levelDiscountType
    };
    switch(item.type) {
      case 'cosmetic':
        return {
          ...defaultItem,
          title: `${item.title}${item.paymentMethod === 'Prepaid' ? `（方案折抵）` : ''}`,
          shortTitle: `${item.qty}<i class="fontello fontello-paw fa-fw ml-2 text-primary"></i>`,
        };
      case 'goods':
        return {
          ...defaultItem,
          store: item.store,
          spec: item.spec,
          class: "isGoods",
          title: `${item.title}${item.paymentMethod === 'Prepaid' ? `（方案折抵）` : ''}`,
          shortTitle: `${item.qty}<i class="fontello fontello-paw fa-fw ml-2 text-primary"></i>`,
        };
      case 'daycare':
        return {
          ...defaultItem,
          title: `${item.title}`,
          shortTitle: `${item.qty}<i class="fontello fontello-paw fa-fw ml-2 text-primary"></i>`,
        };
      case 'prepaid-plan':
        return {
          ...defaultItem,
          title: `${item.qty}<i class="fontello fontello-paw fa-fw ml-2 text-primary"></i>`,
          shortTitle: `${item.qty}<i class="fontello fontello-paw fa-fw ml-2 text-primary"></i>`,
        };
      case 'prepaid':
        return {
          ...defaultItem,
          title: `${item.description} - 抵用1次${isUnpaidAndDeduct ? '(未付款)' : ''}`,
          shortTitle: '包套抵用',
        };
      case 'deposit':
        return {
          ...defaultItem,
          title: '儲值金付款',
          shortTitle: '儲值金付款',
        };
      case 'deposit-plan':
        return {
          ...defaultItem,
          title: '儲值金付款',
          shortTitle: '儲值金付款',
        }; 
      case 'coupon':
        return {
          ...defaultItem,
          title: `優惠券 ${item.title}`,
          shortTitle: '優惠券',
        };
      case 'cosmetic-add':
        return {
          ...defaultItem,
          title: `加購 - ${item.title}`,
          shortTitle: `+${item.title}`,
        };
      case '+':
        return {
          ...defaultItem,
          title: item.title ? `加價-${item.title}` : `加價`,
          shortTitle: `加價`,
        };
      case '-':
        return {
          ...defaultItem,
          title: item.title ? `減價-${item.title}` : `減價`,
          shortTitle: `減價`,
        };
      case '%':
        return {
          ...defaultItem,
          title: item.title ? `折扣-${item.title}` : `折扣`,
          shortTitle: `折扣(${item.description}%)`,
        };
      default:
        return false
    }
  });
}


/**
 * 多隻預約訂單明細
 * @param type
 * @param params
 * @param priceTotal
 * @param prepaid
 * @param isUnpaidAndDeduct
 * @param formatOrder
 * @returns {*}
 */
export function orderBillingReserveList({
  type = 'memberOrder', // [order, memberOrder],
  params,
  priceTotal,
  isUnpaidAndDeduct = false,
  formatOrder = {} // type: order required
} = {}) {
  const paramsList = (typeList) => {
    return orderReserveParamsList({
      typeList,
      params,
      isUnpaidAndDeduct
    });
  };

  const total = params
    .filter(item => {
      return ['cosmetic', 'goods', 'daycare','coupon', 'prepaid', 'deposit','deposit-plan','prepaid-plan','cosmetic-add', '+', '-', '%','daycare-add'].includes(item.type)
    })
    .reduce((previousValue, currentValue) => {
      return previousValue + currentValue.total
    }, 0);
  const subTotal = params
    .filter(item => {
      return ['cosmetic', 'goods', 'coupon', 'prepaid', 'deposit','deposit-plan','prepaid-plan'].includes(item.type)
    })
    .reduce((previousValue, currentValue) => {
      return previousValue + currentValue.total
    }, 0);
  return conditionList([
    ...paramsList(['cosmetic', 'goods', 'coupon', 'prepaid','deposit-plan','prepaid-plan']),
    ...paramsList(['deposit']),
    {
      type: 'hr',
      condition: () => {
        return !!paramsList(['deposit'])[0]
      },
    },
    {
      title: '小計',
      text: `NT$ ${formatCurrency(subTotal)}`,
      textClass: 'font-number',
      condition: () => {
        return !!paramsList(['deposit'])[0]
      },
    },
    ...paramsList(['cosmetic-add', '+', '-', '%','daycare-add']),
    {
      type: 'hr',
      // condition: () => {
      //   return !!paramsList(['cosmetic', 'coupon', 'prepaid', 'deposit','prepaid-plan'])[0]
      // },
    },
    {
      title: '小計',
      text: `NT$ ${formatCurrency(total)}`,
      textClass: 'font-number',
      // condition: () => {
      //   return !!paramsList(['cosmetic', 'coupon', 'prepaid', 'deposit','prepaid-plan'])[0]
      // },
    },
    // {
    //   type: 'hr',
    //   condition: () => {
    //     return !!paramsList(['cosmetic', 'coupon', 'prepaid', 'deposit','deposit-plan','prepaid-plan'])[0]
    //   },
    // },
    // {
    //   title: '總計',
    //   text: `${formatPrice(priceTotal)}`,
    //   textClass: 'font-number',
    //   condition: () => {
    //     return type === 'memberOrder'
    //   },
    // },
    // {
    //   title: `<span class="text-${formatOrder.color} fw-semi-bold">${formatOrder.title}</span><br><span class="text-gray-2 fs-2sm">${formatOrder.paidInfo}</span>`,
    //   text: `${formatPrice(priceTotal)}`,
    //   class: 'align-items-end mt-2',
    //   textClass: `font-number fs-2xl fw-semi-bold text-${formatOrder.infoColor}`,
    //   condition: () => {
    //     return type === 'order'
    //   },
    // }
  ]);
}
